import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Mail, Lock, LogIn } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  // Show a helpful notice when the axios interceptor bounced us here
  // because the JWT expired mid-session.
  //
  // Toaster (in App.js) and this page mount on the SAME React commit,
  // so a synchronous `toast.error` here would race the Sonner listener
  // and be silently dropped (caught by testing-agent iter-22). Deferring
  // one macrotask (setTimeout 60ms) reliably lets the Toaster subscribe
  // before we push the toast — cheap fix, no extra dependencies.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      const t = setTimeout(() => {
        toast.error('Your session has expired. Please sign in again.', {
          id: 'session-expired',
        });
      }, 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-2 border-gray-200 shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_waste-less-hub-1/artifacts/918kjlll_image.png"
              alt="Repair Cafe Logo"
              className="w-20 h-20 rounded-full"
            />
          </div>
          <CardTitle className="text-3xl text-[#193460]">Admin Login</CardTitle>
          <CardDescription className="text-base">
            Sign in to manage repair requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                Password
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-12 border-2 border-gray-300 focus:border-[#E87B6A] transition-colors duration-300"
                required
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              size="lg" 
              className="w-full bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold h-14 text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
            >
              <LogIn className="mr-2 w-5 h-5" />
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <div className="mt-6 text-center border-t pt-4 space-y-2">
            <Link
              to="/admin/forgot-password"
              data-testid="forgot-password-link"
              className="text-sm text-[#E87B6A] hover:underline font-medium inline-block"
            >
              Forgot your password?
            </Link>
            <p className="text-sm text-gray-600">
              Need an account? Contact your administrator.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
